import { useState, useCallback } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { key, useDeletePhytosanitaryProgramApplication } from "@/hooks/planning/phytosanitary-program/usePhytosanitaryProgram";

function removeProgramFromLookups(oldData, programId) {
	if (!oldData) return oldData;
	return {
		...oldData,
		phytosanitary_programs: (oldData.phytosanitary_programs || []).filter((program) => program.value !== programId),
	};
}

function removeApplicationFromList(oldData, programId, applicationNumber) {
	if (!oldData?.data) return oldData;
	return {
		...oldData,
		data: oldData.data.filter((item) => !(item.id === programId && String(item.application_number) === String(applicationNumber))),
	};
}

export function usePhytosanitaryApplicationDeletion({ setInfoMessages, setMessageType, programData }) {
	const [applicationToDelete, setApplicationToDelete] = useState(null);
	const queryClient = useQueryClient();

	const { mutate: mutateDeleteApplication, isLoading: isLoadingDeleteApplication } = useDeletePhytosanitaryProgramApplication();

	const handleDeleteApplication = useCallback((row) => {
		setApplicationToDelete(row);
	}, []);

	const confirmDeleteApplication = useCallback(() => {
		if (!applicationToDelete) return;

		const programId = applicationToDelete.id;
		const applicationNumber = Number(applicationToDelete.application_number);
		const distinctApplicationNumbers = new Set((programData || []).filter((row) => row.id === programId).map((row) => row.application_number));
		const isLastApplication = distinctApplicationNumbers.size === 1;

		queryClient.setQueriesData([key, "list"], (oldData) => removeApplicationFromList(oldData, programId, applicationNumber));

		if (isLastApplication) {
			queryClient.setQueriesData([key, "lookups"], (oldData) => removeProgramFromLookups(oldData, programId));
		}

		mutateDeleteApplication(
			{ programId, applicationNumber },
			{
				onSuccess: () => {
					setApplicationToDelete(null);
					toast.success("Aplicación eliminada correctamente");
				},
				onError: (error) => {
					setApplicationToDelete(null);
					queryClient.invalidateQueries([key, "list"]);
					if (isLastApplication) {
						queryClient.invalidateQueries([key, "lookups"]);
					}
					if (error?.response?.data?.messages && error.response.data.messages.length > 0) {
						setInfoMessages(error.response.data.messages);
						setMessageType(error.response.data.message_type || "error");
					} else {
						toast.error(error?.response?.data?.message || "Error al eliminar la aplicación");
					}
				},
			},
		);
	}, [applicationToDelete, programData, mutateDeleteApplication, queryClient, setInfoMessages, setMessageType]);

	const cancelDeleteApplication = useCallback(() => {
		setApplicationToDelete(null);
	}, []);

	return { applicationToDelete, isLoadingDeleteApplication, handleDeleteApplication, confirmDeleteApplication, cancelDeleteApplication };
}
